import React, { useState, useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { RiSearchLine, RiCloseLine, RiUserLine, RiBuildingLine, RiArrowDownSLine, RiEditLine } from '@remixicon/react';
import { fetchClients } from '../../services/clientService';
import { cn } from '../../../../components/ui/utils';
import { useAuthStore } from '@store/authStore';
import EditClientModal from './EditClientModal';
/**
 * Client Selector Component
 * Dropdown for selecting existing clients
 *
 * @param {Object} props
 * @param {Object|null} props.value - Selected client object
 * @param {Function} props.onChange - Callback when client is selected
 * @param {string} props.error - Error message
 * @param {boolean} props.disabled - Whether selector is disabled
 */
const ClientSelector = ({
  value = null,
  onChange,
  error = '',
  disabled = false
}) => {

  const { userProfile } = useAuthStore();

  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const modalRef = useRef(null);
  const inputRef = useRef(null);
  const errorId = useId();

  // Load clients when dropdown opens
  useEffect(() => {
    if (isOpen && clients.length === 0) {
      loadClients();
    }
  }, [isOpen]);

  // Load all clients
  const loadClients = async () => {
    setIsLoading(true);
    try {
      const results = await fetchClients(0, 100, userProfile.userprofile_id);
      setClients(results);
    } catch (err) {
      console.error('Failed to load clients:', err);
      setClients([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle client selection
  const handleClientSelect = (client) => {
    onChange?.(client);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Handle input click to open dropdown
  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  // Handle click outside modal
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filter clients by search query
  const filteredClients = clients.filter(client => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      client.first_name?.toLowerCase().includes(query) ||
      client.last_name?.toLowerCase().includes(query) ||
      client.company?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query)
    );
  });

  // Get display value
  const getDisplayValue = () => {
    if (value) {
      const firstName = value.first_name || value.firstName || '';
      const lastName = value.last_name || value.lastName || '';
      const email = value.email || '';
      return `${firstName} ${lastName} - ${email}`;
    }
    return '';
  };

  return (
    <div className="relative w-full">
      {/* Input field */}
      <div className="relative">
        <div
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          data-testid="user-icon"
        >
          <RiUserLine className="w-4 h-4 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={getDisplayValue()}
          onClick={handleInputClick}
          placeholder="Select Client"
          readOnly
          disabled={disabled}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? errorId : undefined}
          className={cn(
            'w-full pl-10 pr-10 py-2.5 border rounded-lg text-sm transition-all cursor-pointer',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            error && 'error border-red-500 focus:ring-red-500',
            disabled && 'disabled bg-gray-100 cursor-not-allowed opacity-60',
            !error && !disabled && 'border-gray-300 hover:border-gray-400'
          )}
        />
        {value ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsEditOpen(true);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-primary-500 transition-colors"
            title="Edit client"
          >
            <RiEditLine className="w-4 h-4" />
          </button>
        ) : (
          <div
            className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
            data-testid="chevron-down"
          >
            <RiArrowDownSLine className="w-4 h-4 text-gray-400" />
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div
          id={errorId}
          className="mt-1 text-sm text-red-600"
        >
          {error}
        </div>
      )}

      {/* Modal overlay */}
      {isOpen && !disabled && createPortal(
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
            onClick={() => setIsOpen(false)}
          />

          {/* Modal */}
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label="Select client"
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-md bg-white rounded-lg shadow-2xl z-[9999] max-h-[80vh] flex flex-col"
          >
            {/* Modal header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Select Client</h2>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                aria-label="Close"
              >
                <RiCloseLine className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Search input */}
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <RiSearchLine className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, company, or email..."
                  autoFocus
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Client list */}
            <div
              role="listbox"
              aria-label="Clients"
              className="flex-1 overflow-y-auto"
            >
              {isLoading && (
                <div
                  className="flex flex-col items-center justify-center py-8"
                  role="status"
                  aria-live="polite"
                >
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent mb-2"></div>
                  <span className="text-sm text-gray-500">Loading clients...</span>
                </div>
              )}

              {!isLoading && filteredClients.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  No clients found
                </div>
              )}

              {!isLoading && filteredClients.length > 0 && (
                <div className="divide-y divide-gray-200">
                  {filteredClients.map((client) => (
                    <button
                      key={client.client_id || client.id || client.email}
                      type="button"
                      role="option"
                      aria-label={`${client.first_name} ${client.last_name} - ${client.company || ''}`}
                      aria-selected={value?.client_id === client.client_id || value?.id === client.id}
                      onClick={() => handleClientSelect(client)}
                      className="w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors text-left"
                    >
                      <div
                        className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center"
                        data-testid="client-icon"
                      >
                        <RiUserLine className="w-5 h-5 text-primary-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{client.first_name} {client.last_name}</div>
                        {client.company && (
                          <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500">
                            <RiBuildingLine className="w-3 h-3" />
                            <span className="truncate">{client.company || ""}</span>
                          </div>
                        )}
                        <div className="mt-0.5 text-xs text-gray-500 truncate">{client.email}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="p-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Edit Client Modal */}
      {isEditOpen && value && (
        <EditClientModal
          client={value}
          onClose={() => setIsEditOpen(false)}
          onSaved={(updatedClient) => {
            onChange?.(updatedClient);
            setIsEditOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default ClientSelector;
